import type { AccountBalance } from '@api/hooks/useBalance.ts';
import { useBalance, useTotalBalance } from '@api/hooks/useBalance.ts';
import { EmptyState } from '@components/EmptyState';
import { Skeleton } from '@components/skeleton/Skeleton.tsx';
import type { ColumnProps } from '@gurupay/gp-ui-library';
import { ProgressBar } from '@gurupay/gp-ui-library';
import { Table, Typography } from '@gurupay/gp-ui-library';
import { AccountInfo } from '@pages/accounts/components';
import { AccountCarousel } from '@pages/dashboard/components/TotalBalance/components/AccountCarousel.tsx';
import { formatWithCurrency } from '@utils/utils.ts';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { Link, useNavigate } from 'react-router-dom';

import { AccountStatusChip } from './components/AccountStatusChip.tsx';

const renderers = {
	accountInfo: (accountInfo: AccountBalance) => (
		<AccountInfo {...accountInfo} showLabel={false} />
	),
	balance: ({ balance, currency }: AccountBalance) => (
		<span className="whitespace-nowrap">
			{formatWithCurrency({ value: balance, currency })}
		</span>
	),
	availableBalance: ({ available, currency }: AccountBalance) => (
		<span className="whitespace-nowrap">
			{formatWithCurrency({ value: available, currency })}
		</span>
	),
	accountStatus: ({ status }: AccountBalance) => <AccountStatusChip status={status} />,
};

export const TotalBalance = () => {
	const { t } = useTranslation('', { keyPrefix: 'totalBalance' });
	const navigate = useNavigate();
	const isMobile = useMediaQuery({ query: '(max-width: 480px)' });
	const { data, isLoading } = useTotalBalance();
	const { data: accounts, isLoading: isLoadingTable } = useBalance({});
	const rows = accounts?.items ?? [];
	const columns: ColumnProps[] = [
		{ label: t('account'), key: 'accountInfo', align: 'left' },
		{ label: '', key: 'accountStatus', align: 'left' },
		{ label: t('label'), key: 'balance', align: 'right' },
		{ label: t('availableTotal'), key: 'availableBalance', align: 'right' },
	];

	const handleRowClick = ({ uuid }: AccountBalance) => {
		navigate(`/accounts/${uuid}`);
	};

	return (
		<div className="max-mobile:p-space-16 p-space-24 bg-white rounded-md max-mobile:space-y-space-8 space-y-space-16 h-full">
			<div className="flex w-full items-center justify-between">
				<Typography className="font-medium text-neutral-800" level="small-text">
					{t('label')}
				</Typography>
				<Link
					className="text-sm-bold text-brand-secondary-600 hover:text-neutral-950 border-b border-b-transparent hover:border-b-neutral-950 transition-all duration-300"
					to="/accounts"
				>
					{t('allAccounts')}
				</Link>
			</div>

			<div className="max-mobile:text-[24px] text-[36px] font-bold leading-1.2 text-neutral-800 mt-space-12 whitespace-nowrap">
				{isLoading ? (
					<Skeleton className="w-full h-13.5 max-mobile:h-9" />
				) : (
					formatWithCurrency({
						value: data?.balance ?? '',
						currency: data?.currency ?? '',
					})
				)}
			</div>

			<div className="flex gap-space-24 justify-between">
				<div className="flex flex-col max-mobile:gap-y-space-2 gap-y-space-8">
					<Typography className="font-medium" level="small-text">
						{t('availableTotal')}
					</Typography>
					<div className="max-mobile:text-base-bold text-xl-bold whitespace-nowrap">
						{isLoading ? (
							<Skeleton className="w-full h-6.5" />
						) : (
							formatWithCurrency({
								value: data?.available ?? '',
								currency: data?.currency ?? '',
							})
						)}
					</div>
				</div>
				<div className="flex flex-col max-mobile:gap-y-space-2 gap-y-space-8 items-end">
					<Typography className="font-medium" level="small-text">
						{t('reservedTotal')}
					</Typography>
					<div className="max-mobile:text-base-bold text-xl-bold whitespace-nowrap">
						{isLoading ? (
							<Skeleton className="w-full h-6.5" />
						) : (
							formatWithCurrency({
								value: data?.reserved ?? '',
								currency: data?.currency ?? '',
							})
						)}
					</div>
				</div>
			</div>
			<ProgressBar
				isLoading={isLoading}
				max={data?.balance === '0.00' ? 1 : data?.balance}
				value={data?.available ?? 0}
			/>
			{isMobile ? (
				<AccountCarousel />
			) : (
				<Table<AccountBalance>
					className="p-space-8 [&_td]:p-space-8 [&_th]:p-space-8 [&_tr:hover_.approval-icon]:bg-neutral-100"
					columns={columns}
					emptyTablePlaceholder={
						<EmptyState
							subTitle={t('noAccountsDescription')}
							title={t('noAccountsTitle')}
						/>
					}
					isLoading={isLoadingTable}
					onRowClick={handleRowClick}
					renders={renderers}
					rows={rows}
				/>
			)}
		</div>
	);
};
